import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { User, Mail, Lock, Eye, EyeOff, UserPlus, AlertCircle } from 'lucide-react';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import { Button } from '../components/ui/Button';
import SEO from '../components/seo/SEO';
import { registerUser } from '../store/actions/authActions';

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Main = styled.main`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.25rem 4rem;
`;

const Card = styled.div`
  width: 100%;
  max-width: 25rem;
  margin: 0 auto;
  padding: 2.5rem 2rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadows.medium};

  @media (max-width: 480px) {
    padding: 2rem 1.25rem;
    box-shadow: ${({ theme }) => theme.shadows.soft};
  }
`;

const BrandBadge = styled.div`
  width: 3rem;
  height: 3rem;
  margin: 0 auto 1.25rem;
  display: grid;
  place-items: center;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
`;

const Title = styled.h1`
  margin: 0 0 0.4rem;
  text-align: center;
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const Sub = styled.p`
  margin: 0 0 1.75rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: 0.9375rem;
  line-height: 1.5;
`;

const Field = styled.label`
  display: block;
  margin-bottom: 1.1rem;
`;

const FieldLabel = styled.span`
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
`;

const InputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg.field-icon {
    position: absolute;
    left: 0.85rem;
    color: ${({ theme }) => theme.colors.mutedForeground};
    pointer-events: none;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.7rem 0.85rem 0.7rem 2.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.colors.input};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.foreground};
  font-size: 0.9375rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.mutedForeground};
    opacity: 0.7;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.mutedForeground};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.ring};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.ring}26;
  }

  &.has-toggle {
    padding-right: 2.75rem;
  }
`;

const ToggleBtn = styled.button`
  position: absolute;
  right: 0.5rem;
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.mutedForeground};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.sm};

  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Hint = styled.span`
  display: block;
  margin-top: 0.35rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const ErrorText = styled.p`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1.1rem;
  padding: 0.65rem 0.85rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.error}14;
  border: 1px solid ${({ theme }) => theme.colors.error}33;
  color: ${({ theme }) => theme.colors.error || '#dc2626'};
  font-size: 0.875rem;

  svg {
    flex-shrink: 0;
  }
`;

const FooterText = styled.p`
  margin: 1.5rem 0 0;
  text-align: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export default function Register() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { registerLoading, error, isAuthenticated } = useSelector((s) => s.auth);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [didRegister, setDidRegister] = useState(false);

  const redirectTo = location.state?.from?.pathname || '/account';

  // Redirect if already signed in (not mid-registration).
  useEffect(() => {
    if (isAuthenticated && !didRegister) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, didRegister, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setDidRegister(true);
      const data = await dispatch(
        registerUser({
          displayName: displayName.trim(),
          email: email.trim(),
          password,
        })
      );
      navigate('/account', {
        replace: true,
        state: {
          justRegistered: true,
          emailSent: Boolean(data?.emailSent),
          registerMessage: data?.message,
        },
      });
    } catch {
      setDidRegister(false);
    }
  };

  return (
    <PageWrapper>
      <SEO title={t('auth.registerTitle')} description={t('auth.registerSub')} robots="noindex, follow" />
      <Header />
      <Main>
        <Card>
          <BrandBadge>
            <UserPlus size={22} />
          </BrandBadge>
          <Title>{t('auth.registerTitle')}</Title>
          <Sub>{t('auth.registerSub')}</Sub>
          <form onSubmit={handleSubmit}>
            {error ? (
              <ErrorText>
                <AlertCircle size={16} />
                {error}
              </ErrorText>
            ) : null}
            <Field>
              <FieldLabel>{t('auth.displayNameLabel')}</FieldLabel>
              <InputWrap>
                <User size={18} className="field-icon" />
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  maxLength={50}
                  autoComplete="name"
                  placeholder={t('auth.displayNameLabel')}
                />
              </InputWrap>
            </Field>
            <Field>
              <FieldLabel>{t('auth.emailLabel')}</FieldLabel>
              <InputWrap>
                <Mail size={18} className="field-icon" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              </InputWrap>
            </Field>
            <Field>
              <FieldLabel>{t('auth.passwordLabel')}</FieldLabel>
              <InputWrap>
                <Lock size={18} className="field-icon" />
                <Input
                  className="has-toggle"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="••••••••"
                />
                <ToggleBtn
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </ToggleBtn>
              </InputWrap>
              <Hint>{t('auth.passwordHint')}</Hint>
            </Field>
            <Button type="submit" disabled={registerLoading} style={{ width: '100%' }}>
              {registerLoading ? t('auth.creatingAccount') : t('auth.createAccount')}
            </Button>
          </form>
          <FooterText>
            {t('auth.haveAccount')}{' '}
            <Link to="/login" state={location.state}>
              {t('auth.signIn')}
            </Link>
            {' · '}
            <Link to="/forgot-password">{t('auth.forgotPasswordLink')}</Link>
          </FooterText>
        </Card>
      </Main>
      <Footer />
    </PageWrapper>
  );
}
